import type {
  AnalyticsWidgetResult,
  GradeWiseParticipationItem,
  SchoolReportData,
  SchoolReportGradeBlock,
  SchoolReportMetadata,
  SchoolReportParticipationDetails,
  SchoolReportStudentRow,
  SchoolReportSubjectStats,
  SchoolReportSummaryItem,
  SchoolReportWidgetBundles,
} from '@/types/schoolReport';

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.trim().replace(/%$/, '').replace(/,/g, '');
    if (cleaned !== '' && !Number.isNaN(Number(cleaned))) return Number(cleaned);
  }
  return fallback;
}

function asString(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function asOptionalString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  return String(value);
}

function normalizeGrade(value: unknown): string {
  const raw = asString(value).trim();
  if (!raw) return 'Grade-1';
  if (/^grade[-\s]?\d+/i.test(raw)) {
    const num = raw.match(/\d+/)?.[0] ?? '1';
    return `Grade-${num}`;
  }
  if (/^\d+$/.test(raw)) return `Grade-${raw}`;
  return raw;
}

function gradeSortValue(grade: string): number {
  const n = Number(grade.match(/\d+/)?.[0]);
  return Number.isFinite(n) ? n : 999;
}

function formatPercent(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const n = asNumber(value, Number.NaN);
  if (Number.isNaN(n)) return asString(value);
  return `${Number(n.toFixed(1))}%`;
}

/** TOTAL_TIME from API looks like fractional hours → "Xm Ys". */
function formatTotalTime(value: unknown): string {
  const hours = asNumber(value, Number.NaN);
  if (Number.isNaN(hours) || hours <= 0) return '—';
  const totalSeconds = Math.round(hours * 3600);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m <= 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/**
 * BEST_CHAPTERS / IMPROVEMENT_CHAPTERS arrive as JSON strings of nested arrays:
 * [["Motion in a Plane"], ["Laws of Motion"]]
 */
function parseChapterLists(value: unknown): string[] {
  if (Array.isArray(value)) {
    return flattenChapterNodes(value);
  }
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) return flattenChapterNodes(parsed);
  } catch {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function flattenChapterNodes(nodes: unknown[]): string[] {
  const out: string[] = [];
  for (const node of nodes) {
    if (typeof node === 'string' && node.trim()) out.push(node.trim());
    else if (Array.isArray(node)) out.push(...flattenChapterNodes(node));
  }
  return [...new Set(out)];
}

function buildParticipation(
  gradewiseRows: Record<string, unknown>[]
): SchoolReportParticipationDetails {
  const gradewise_participation: GradeWiseParticipationItem[] = gradewiseRows
    .map((row) => {
      const participated = asNumber(row.GRADEWISE_PARTICIPATION);
      const registered = Math.max(asNumber(row.GRADEWISE_REGISTRATIONS), participated);
      return {
        grade: normalizeGrade(row.GRADE_NAME ?? row.GRADE),
        participated,
        registered,
      };
    })
    .sort((a, b) => gradeSortValue(a.grade) - gradeSortValue(b.grade));

  const sample = gradewiseRows[0] ?? {};
  const overallParticipated = asNumber(sample.OVERALL_PARTICIPATIONS, 
    gradewise_participation.reduce((s, g) => s + g.participated, 0)
  );
  const overallRegistered = asNumber(
    sample.OVERALL_REGISTRATIONS,
    gradewise_participation.reduce((s, g) => s + g.registered, 0)
  );
  const overallPercent = asNumber(
    sample.OVERALL_PARTICIPATION_PERCENT,
    overallRegistered > 0
      ? Math.round((overallParticipated / overallRegistered) * 1000) / 10
      : 0
  );
  const otherSchoolsAvg = asNumber(sample.OTHER_SCHOOLS_PERCENT, overallPercent);

  return {
    total_participation: `${overallParticipated}/${overallRegistered}`,
    overall_participation_percent: overallPercent,
    other_schools_avg_percent: otherSchoolsAvg,
    gradewise_participation,
  };
}

function buildSubjectFromSectionRow(row: Record<string, unknown>): {
  name: string;
  stats: SchoolReportSubjectStats;
} {
  const name = asString(row.SUBJECT, 'Subject');
  const accuracy = formatPercent(row.SUB_ACCURACY);
  const otherAvg = formatPercent(row.SUB_OTHER_SCHOOL_AVG);

  return {
    name,
    stats: {
      max_marks: asNumber(row.SUB_MAX_MARKS),
      avg_marks: asNumber(row.SUB_AVG_MARKS),
      total_marks: asNumber(row.SUB_TOTAL_MARKS),
      accuracy,
      other_school_avg: otherAvg,
      best_chapters: parseChapterLists(row.BEST_CHAPTERS),
      improvement_chapters: parseChapterLists(row.IMPROVEMENT_CHAPTERS),
    },
  };
}

function isAttemptedStatus(status: unknown): boolean {
  const s = asString(status).trim().toLowerCase();
  if (!s) return false;
  return s === 'attempted' || s === 'submitted' || s === 'completed';
}

function buildStudentFromRow(row: Record<string, unknown>): SchoolReportStudentRow {
  const current = asNumber(row.CURRENT_MARKS);
  const total = asNumber(row.TOTAL_MARKS);
  const marksObtained =
    total > 0 ? `${current}/${total}` : current > 0 ? String(current) : '—';

  return {
    rank: asString(row.RANK, '—'),
    student_name: asString(row.STUDENT_NAME, '—'),
    marks_obtained: marksObtained,
    accuracy: formatPercent(row.ACCURACY) ?? '—',
    total_time: formatTotalTime(row.TOTAL_TIME),
  };
}

/**
 * One UI block per (grade, exam) — matches GradeWiseDetails exam dropdown.
 */
function buildGradewiseData(bundle: SchoolReportWidgetBundles): SchoolReportGradeBlock[] {
  const { sectionwiseRows, studentwiseRows } = bundle;
  const blocks = new Map<string, SchoolReportGradeBlock>();

  const ensureBlock = (grade: string, examName?: string): SchoolReportGradeBlock => {
    const key = `${grade}::${examName ?? ''}`;
    let block = blocks.get(key);
    if (!block) {
      block = {
        grade,
        exam_name: examName || undefined,
        subjects: {},
        student_performance: {
          total_attempted: 0,
          total_not_attempted: 0,
          attempted: [],
          not_attempted: [],
        },
      };
      blocks.set(key, block);
    }
    return block;
  };

  for (const row of sectionwiseRows) {
    const grade = normalizeGrade(row.GRADE_NAME ?? row.GRADE);
    const examName = asOptionalString(row.EXAM_NAME);
    const block = ensureBlock(grade, examName);
    const { name, stats } = buildSubjectFromSectionRow(row);
    block.subjects = { ...(block.subjects ?? {}), [name]: stats };
  }

  for (const row of studentwiseRows) {
    const grade = normalizeGrade(row.GRADE_NAME ?? row.GRADE);
    const examName = asOptionalString(row.EXAM_NAME);
    // Attach students to matching exam block when present; else grade-only block
    let block = blocks.get(`${grade}::${examName ?? ''}`);
    if (!block && examName) {
      // Prefer an existing grade block for this exam created from sectionwise
      block = ensureBlock(grade, examName);
    } else if (!block) {
      block = ensureBlock(grade, examName);
    }

    const student = buildStudentFromRow(row);
    const perf = block.student_performance!;
    if (isAttemptedStatus(row.SUBMISSION_STATUS)) {
      perf.attempted = [...(perf.attempted ?? []), student];
    } else {
      perf.not_attempted = [...(perf.not_attempted ?? []), student];
    }
    perf.total_attempted = perf.attempted?.length ?? 0;
    perf.total_not_attempted = perf.not_attempted?.length ?? 0;
  }

  // If sectionwise empty but we have gradewise grades, still expose empty grade shells
  if (blocks.size === 0) {
    for (const row of bundle.gradewiseRows) {
      ensureBlock(normalizeGrade(row.GRADE_NAME ?? row.GRADE));
    }
  }

  return [...blocks.values()].sort((a, b) => {
    const g = gradeSortValue(a.grade) - gradeSortValue(b.grade);
    if (g !== 0) return g;
    return asString(a.exam_name).localeCompare(asString(b.exam_name));
  });
}

function buildSummary(bundle: SchoolReportWidgetBundles): SchoolReportSummaryItem[] {
  const participation = buildParticipation(bundle.gradewiseRows);
  const [attempted, registered] = (participation.total_participation ?? '0/0').split('/');
  const gradeCount = participation.gradewise_participation?.length ?? 0;

  return [
    {
      participation_summary: `Your school had ${attempted} students participating out of ${registered} registered (${Number(
        participation.overall_participation_percent ?? 0
      ).toFixed(1)}% participation) across ${gradeCount} grade(s).`,
      best_grade_performance:
        gradeCount > 0
          ? `Participation data is available for ${gradeCount} grade(s) based on the schoolwise analytics.`
          : undefined,
      subject_performance:
        bundle.sectionwiseRows.length > 0
          ? `Section/subject breakdown is available for ${bundle.sectionwiseRows.length} subject row(s).`
          : undefined,
      improvement_recommendation:
        bundle.studentwiseRows.length > 0
          ? `Review studentwise performance (${bundle.studentwiseRows.length} student row(s)) to identify improvement areas.`
          : undefined,
    },
  ];
}

function buildMetadata(
  schoolId: string,
  bundle: SchoolReportWidgetBundles
): SchoolReportMetadata {
  const sample =
    bundle.gradewiseRows[0] ?? bundle.sectionwiseRows[0] ?? bundle.studentwiseRows[0] ?? {};
  return {
    school_name: asString(sample.SCHOOL_NAME ?? sample.SCHOOL, schoolId),
    event_name: asString(sample.EVENT_NAME, 'School'),
    level: asOptionalString(sample.LEVEL),
    round: asOptionalString(sample.ROUND),
    event_mode: asOptionalString(sample.EVENT_MODE),
    event_scheduled_date_time: asOptionalString(
      sample.SUBMITTED_DATE ?? sample.MAX_SUBMITTED_AT ?? sample.EXAM_DATE
    ),
  };
}

export function mapWidgetsToSchoolReportData(
  schoolId: string,
  bundle: SchoolReportWidgetBundles
): SchoolReportData {
  return {
    metadata: buildMetadata(schoolId, bundle),
    summary: buildSummary(bundle),
    participation_details: [buildParticipation(bundle.gradewiseRows)],
    gradewise_data: buildGradewiseData(bundle),
  };
}

function asRowArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row)
  );
}

/** Unwrap analytics /widgets payload into query_output rows. */
export function extractQueryOutput(raw: unknown, widgetId: string): Record<string, unknown>[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    if (raw.length === 0) return [];
    const first = raw[0];
    if (
      first &&
      typeof first === 'object' &&
      !('widget_id' in first) &&
      !('query_output' in first)
    ) {
      return asRowArray(raw);
    }
    const results = raw as AnalyticsWidgetResult[];
    const match = results.find((item) => item.widget_id === widgetId) ?? results[0];
    return asRowArray(match?.query_output);
  }

  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.query_output)) return asRowArray(obj.query_output);
    if (Array.isArray(obj.data)) return extractQueryOutput(obj.data, widgetId);
    if (Array.isArray(obj.result)) return extractQueryOutput(obj.result, widgetId);
    if (Array.isArray(obj.results)) return extractQueryOutput(obj.results, widgetId);
  }

  return [];
}
